import { IDynamicPartData } from '../entities';
import { ERoleHatItemId } from '../enums';
import { MatchType, MatchTypeWire, PlayerGrouping } from '../game-settings';

import { IEnginePrepConfig, IMatchmakingConfig } from './map-matchmaking-config';

export interface IMapConfigEntry {
  id: string;
  type: MatchTypeWire;
  name: string;
  assetName: string;
  teamCount: number;
  teamSize: number;
  playerGrouping: PlayerGrouping;
  isAvailable: boolean;
  unlockedByMapId?: string;
  matchmakingConfig?: IMatchmakingConfig;
  enginePrepConfig?: IEnginePrepConfig;
  lobbyConfig?: {
    title: string;
    subtitle: string;
    dataKey: string;
    unlockTooltip?: string;
  };
}

export interface ILane {
  id: string;
  team: number;
  points: { x: number; y: number }[];
}

/**
 * Map entity interface with loose string typing for id.
 * For type-safe entity IDs, use IMapEntity from @nova/configs instead.
 */
export interface IMapEntityLoose {
  id: string;
  x: number;
  y: number;
  tutorialTag?: string;
  team: number;
}

/**
 * Map entity bot interface with loose string typing for id.
 * For type-safe entity IDs, use IMapEntityBot from @nova/configs instead.
 */
export interface IMapEntityBotLoose extends IMapEntityLoose {
  parts?: Record<string, IDynamicPartData>;
  roleSkill: ERoleHatItemId;
}

export interface ISpawnSetLoose {
  team: number;
  laneId: string;
  entities: IMapEntityLoose[];
}

export interface ISpawnConfigLoose {
  spawnInterval: number;
  spawnSets: ISpawnSetLoose[];
}

export interface IRoomLoose {
  initEntities: IMapEntityLoose[];
  initBots?: IMapEntityBotLoose[];
}

export type RoomSetLoose = { [roomIndex: string]: IRoomLoose | undefined };

export interface IMapConfigEntryOrLoose extends IMapConfigEntry {
  rooms: RoomSetLoose;
}

export function isMapConfigEntryOrLoose(mapConfig: IMapConfigEntry): mapConfig is IMapConfigEntryOrLoose {
  // MatchType values are defined equal to MatchTypeWire literals (outpostRuns === 'trenchRun'); cast is TS-only.
  return mapConfig.type === (MatchType.outpostRuns as MatchTypeWire)
    || 'rooms' in mapConfig;
}

export interface IMapConfigEntryDomination extends IMapConfigEntry {
  afkTimer?: number;
  gamePlayConfig: {
    [key: string]: number;
  };
  uiConfigs: IUIConfigDomination;
}

export function isMapConfigEntryZoneRush(mapConfig: IMapConfigEntry): mapConfig is IMapConfigEntryDomination {
  // MatchType values are defined equal to MatchTypeWire literals (zoneRush === 'captureMode'); cast is TS-only.
  return mapConfig.type === (MatchType.zoneRush as MatchTypeWire);
}

export interface IMiniMapInfo {
  textureName: string;
  bgFrame: string;
  gameMapDimensions: {
    width: number;
    height: number;
  };
  entityZIndex: Record<string, number>;
}

export interface IUIConfigDomination {
  countDownTimes: number; // Countdown X seconds
  matchStartUiDelay: number; // X seconds delay before showing the match start UI sequence
  matchStartSoonUiDuration: number; // X seconds delay before starting countdown
  countdownInterval: number; // X second interval for countdown
  matchBeginDisplayDuration: number; // X seconds to display Match Begins!
  playerDisconnectDisplayDuration: number; // Display player disconnect message for X seconds

  miniMap: IMiniMapInfo;
}
